<script>
import { env } from '@shared/env';
import TalkizyAPI from '@app/services/API/Talkizy';
import utilsMixin from '@shared/mixins/utils';
import StatisticCard from '@shared/components/StatisticCard.vue';

export default {
  name: 'ClasseVirtuelleMeetingStatistiques',
  mixins: [utilsMixin],
  components: { StatisticCard },
  data() {
    return {
      meeting: null,
      isLoading: false,
    };
  },
  computed: {
    talkizyMeetingURL() {
      const slug = this.meeting && this.meeting.slug;
      return slug ? `${env.talkizyURL}/meetings/${slug}` : null;
    },
    stats() {
      return this.meeting ? this.meeting.stats : null;
    },
    isEnded() {
      return this.meeting && this.meeting.status === 'ended';
    },
    plannedDurationLabel() {
      const min = this.meeting && this.meeting.duration;
      if (!min) return '—';
      const h = Math.floor(min / 60);
      const m = min % 60;
      return h > 0 ? `${h} h ${m} min` : `${m} min`;
    },
    durationLabel() {
      const s = this.stats && this.stats.duration_seconds;
      if (!s) return '—';
      // Même formatage que le temps de présence d'un apprenant (minutes +
      // secondes), au lieu d'un arrondi à la minute qui rognait les secondes.
      return utilsMixin.filters.formatSeconds(s);
    },
    realTimeRangeLabel() {
      const start = this.stats && this.stats.real_starts_at;
      const end = this.stats && this.stats.real_ends_at;
      if (!start || !end) return null;
      const fmt = (v) => this.$moment(v).format('DD/MM/YY [à] HH:mm');
      return `Du ${fmt(start)} au ${fmt(end)}`;
    },
    attendanceLabel() {
      if (!this.stats) return '—';
      const rate = this.stats.attendance_rate;
      const pct = rate !== null && rate !== undefined ? ` (${Math.round(rate * 100)} %)` : '';
      // attendees_count / invited_count sont déjà des compteurs d'apprenants
      // (l'animateur et les co-animateurs sont exclus côté back).
      return `${Math.max(0, this.stats.attendees_count)} / ${Math.max(0, this.stats.invited_count)}${pct}`;
    },
    satisfactionLabel() {
      if (!this.stats || this.stats.satisfaction_average === null) return '—';
      return `${this.stats.satisfaction_average} / 5 (${this.stats.satisfaction_count} avis)`;
    },
    chatMessagesLabel() {
      if (!this.stats || this.stats.chat_messages_count === null
        || this.stats.chat_messages_count === undefined) return '—';
      return `${this.stats.chat_messages_count}`;
    },
    raisedHandsLabel() {
      if (!this.stats || this.stats.raised_hands_count === null
        || this.stats.raised_hands_count === undefined) return '—';
      return `${this.stats.raised_hands_count}`;
    },
    satisfactionCount() {
      return (this.stats && this.stats.satisfaction_count) || 0;
    },
    satisfactionDistribution() {
      return (this.stats && this.stats.satisfaction_distribution) || {};
    },
  },
  mounted() {
    this.fetchMeeting();
  },
  methods: {
    async fetchMeeting() {
      this.isLoading = true;
      try {
        this.meeting = await TalkizyAPI.getMeeting(this.$route.params.meetingId);
      } catch (e) {
        this.$showMessage.error('Impossible de charger les statistiques.');
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <b-skeleton v-if="isLoading" height="120" :count="2" :animated="true" />

    <div v-else-if="!isEnded" class="box has-text-centered has-text-black">
      Les statistiques seront disponibles une fois la classe virtuelle terminée.
    </div>

    <template v-else>
      <section class="mb-5">
        <h2 class="title is-5 mb-3">
          Statistiques globales
        </h2>
        <ul class="columns is-multiline">
          <li class="column is-4">
            <StatisticCard class="h-full">
              <template #title>
                Durée prévisionnelle
              </template>
              <template #tooltipContent>
                Durée planifiée à la création de la classe virtuelle.
              </template>
              <template #default>
                {{ plannedDurationLabel }}
              </template>
            </StatisticCard>
          </li>
          <li class="column is-4">
            <StatisticCard class="h-full">
              <template #title>
                Durée réelle
              </template>
              <template #tooltipContent>
                Durée effective de la visio (du début à la fin de la salle).
              </template>
              <template #default>
                <!-- append-to-body pour passer au-dessus des cartes voisines ;
                     animation coupée car Buefy met le wrapper en z-index -1 dès
                     la fermeture, ce qui fait déborder le fondu derrière les box. -->
                <b-tooltip
                  v-if="realTimeRangeLabel"
                  :label="realTimeRangeLabel"
                  position="is-top"
                  append-to-body
                  :animated="false"
                >
                  {{ durationLabel }}
                </b-tooltip>
                <template v-else>
                  {{ durationLabel }}
                </template>
              </template>
            </StatisticCard>
          </li>
          <li class="column is-4">
            <StatisticCard class="h-full">
              <template #title>
                Total des participants
              </template>
              <template #tooltipContent>
                Nombre de participants ayant rejoint la visio sur le nombre d'invités.
              </template>
              <template #default>
                {{ attendanceLabel }}
              </template>
            </StatisticCard>
          </li>
          <li class="column is-4">
            <StatisticCard class="h-full">
              <template #title>
                Satisfaction globale
              </template>
              <template #tooltipContent>
                Note moyenne de satisfaction laissée par les participants.
              </template>
              <template #default>
                {{ satisfactionLabel }}
              </template>
            </StatisticCard>
          </li>
          <li class="column is-4">
            <StatisticCard class="h-full">
              <template #title>
                Total de messages du chat
              </template>
              <template #tooltipContent>
                Nombre de messages envoyés dans le chat public.
              </template>
              <template #default>
                {{ chatMessagesLabel }}
              </template>
            </StatisticCard>
          </li>
          <li class="column is-4">
            <StatisticCard class="h-full">
              <template #title>
                Total des mains levées
              </template>
              <template #tooltipContent>
                Nombre total de mains levées par les participants.
              </template>
              <template #default>
                {{ raisedHandsLabel }}
              </template>
            </StatisticCard>
          </li>
        </ul>
      </section>

      <section class="mb-5">
        <h2 class="title is-5 mb-3">
          Satisfaction à chaud
        </h2>
        <div class="box">
          <div v-if="satisfactionCount" class="columns is-vcentered">
            <div class="column is-4 has-text-centered">
              <p class="title is-2 mb-2">
                {{ stats.satisfaction_average }}
                <span class="is-size-5 has-text-grey-dark">/ 5</span>
              </p>
              <b-rate
                class="is-justify-content-center"
                :value="stats.satisfaction_average * 1"
                disabled
              />
              <p class="is-size-8 has-text-grey-dark">
                {{ satisfactionCount }} avis
              </p>
            </div>
            <div class="column is-8">
              <div
                v-for="note in [5, 4, 3, 2, 1]"
                :key="note"
                class="is-flex is-align-items-center mb-2"
              >
                <span class="cvmstats_note is-size-8 mr-3">
                  {{ note }}
                  <b-icon icon="star" size="is-small" type="is-warning" />
                </span>
                <b-progress
                  class="is-flex-grow-1 mb-0"
                  :value="satisfactionDistribution[note] || 0"
                  :max="satisfactionCount"
                  size="is-small"
                  type="is-primary"
                />
                <span class="cvmstats_count is-size-8 ml-3 has-text-grey-dark">
                  {{ satisfactionDistribution[note] || 0 }}
                </span>
              </div>
            </div>
          </div>
          <p v-else class="has-text-grey-dark">
            Aucune réponse de satisfaction pour cette classe virtuelle.
          </p>
        </div>
      </section>

<!--      <div v-if="stats && stats.recordings && stats.recordings.length" class="box">-->
<!--        <h2 class="subtitle">-->
<!--          Enregistrements-->
<!--        </h2>-->
<!--        <ul>-->
<!--          <li v-for="rec in stats.recordings" :key="rec.id">-->
<!--            <a :href="rec.url" target="_blank" rel="noopener">-->
<!--              <b-icon icon="video" size="is-small" />-->
<!--              Enregistrement-->
<!--            </a>-->
<!--          </li>-->
<!--        </ul>-->
<!--      </div>-->
      <p v-if="talkizyMeetingURL" class="has-text-centered has-text-grey-dark is-size-8 mt-8">
        Retrouvez le détail complet des participants et des statistiques avancées
        sur la page de cette classe virtuelle dans Talkizy.
        <a :href="talkizyMeetingURL" target="_blank" rel="noopener">
          Voir plus de statistiques sur Talkizy
          <b-icon icon="external-link-alt" size="is-small" />
        </a>
      </p>
    </template>
  </div>
</template>

<style lang="scss" scoped>
// Largeurs fixes sur les libellés pour que les barres de répartition
// commencent et finissent toutes au même niveau.
.cvmstats_note {
  width: 40px;
  flex-shrink: 0;
  white-space: nowrap;
}

.cvmstats_count {
  width: 28px;
  flex-shrink: 0;
  text-align: right;
}
</style>
